// Recent transactions list — top N from a transactions array.
// Compact row, 1 line per tx.
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../lib/format.js';
import EmptyState from './EmptyState.jsx';
import { useI18n } from '../i18n/index.jsx';
import { categoryLabel } from '../lib/categories.js';
import { Money } from '../lib/privacy.jsx';

export default function RecentTransactions({ transactions = [], currency = 'HKD', limit = 5, onViewAll }) {
  const navigate = useNavigate();
  const { t } = useI18n();
  if (transactions.length === 0) {
    return (
      <EmptyState
        icon="💼"
        title={t('tx.empty')}
        size="sm"
        testid="recent-tx-empty"
      />
    );
  }
  const shown = transactions.slice(0, limit);
  return (
    <div>
      <ul className="divide-y divide-slate-100 dark:divide-slate-700">
        {shown.map(tx => {
          const isExpense = tx.type === 'expense';
          return (
            <li key={tx.id} className="flex items-center gap-2 py-2 text-sm">
              <span
                className="w-7 h-7 rounded-full flex items-center justify-center text-sm shrink-0"
                style={{ backgroundColor: tx.category_color + '20' }}
                aria-hidden="true"
              >
                {tx.category_icon}
              </span>
              <span className="flex-1 truncate text-slate-700 dark:text-slate-200">{categoryLabel({ name: tx.category_name }, t)}</span>
              <span className="text-xs text-slate-400 dark:text-slate-500 hidden sm:inline">
                {formatDate(tx.occurred_on)}
              </span>
              {/* Phase 54: tag chips on Dashboard recent widget are now
                  <button> with onClick → navigate to /transactions?tag=…
                  This mirrors the Transactions page chip behaviour so users
                  learn one interaction model that works everywhere.
                  Bumped min-h-[24px]→[32px] + brand border for accessibility. */}
              {tx.tags && (
                <span className="flex flex-wrap gap-1 max-w-[160px]">
                  {tx.tags.split(',').slice(0, 2).map((tag, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={(e) => { e.stopPropagation(); navigate(`/transactions?tag=${encodeURIComponent(tag)}`); }}
                      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] min-h-[32px] border bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-200 border-brand-200 dark:border-brand-800 hover:bg-brand-100 dark:hover:bg-brand-900/50"
                      aria-label={`${t('tx.tagLabel')}: ${tag}`}
                      data-testid={`recent-tx-tag-${tag}`}
                    >
                      <span aria-hidden="true" className="opacity-70">🔍</span>
                      {tag}
                    </button>
                  ))}
                </span>
              )}
              <span className={`font-medium tabular-nums ${isExpense ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                {isExpense ? '-' : '+'}<Money amount={tx.amount} currency={currency} className={`tabular-nums ${isExpense ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`} />
              </span>
            </li>
          );
        })}
      </ul>
      {transactions.length > limit && onViewAll && (
        <button
          onClick={onViewAll}
          className="mt-2 w-full text-sm text-brand-600 dark:text-brand-300 hover:bg-brand-50 dark:hover:bg-brand-900/30 py-2 rounded-md min-h-[44px]"
        >
          {t('tx.seeAllFmt', { count: transactions.length })} →
        </button>
      )}
    </div>
  );
}
